import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiClock, FiCheckCircle, FiAlertTriangle, FiRefreshCw } from 'react-icons/fi';
import { upsertMyToolWithSkillId } from '../../services/myToolsApi';
import AgentBundlePreview from '../../components/AgentBundlePreview';
import ShareResolveDetailView from '../../components/ShareResolveDetailView';
import { extractSkillDescription } from '../../utils/skillFrontmatter';
import { resolveAgentShare, importAgentShare, addMcpCredential, attachExistingMcpServer, type AgentImportResult, type AgentShareSnapshot, type ImportedIntegration, } from '../claudeagents/claudeAgentApi';
import { saveMyCredential } from '../apiintegrations/apiIntegrationService';
import { copyAgentLogoFromShare } from '../../services/agentLogoService';
import { ensureAgentLogosLoaded } from '../../utils/agentPhotos';
import { ROUTES } from '../../app/routes';
import '../../styles/MyTools.css';
type Status = 'loading' | 'preview' | 'copying-skills' | 'creating-agent' | 'done' | 'error';
const ShareAgentResolvePage: React.FC = () => {
    const { token } = useParams<{
        token: string;
    }>();
    const navigate = useNavigate();
    const [status, setStatus] = useState<Status>('loading');
    const [error, setError] = useState('');
    const [snapshot, setSnapshot] = useState<AgentShareSnapshot | null>(null);
    const [agentName, setAgentName] = useState('');
    const [nameError, setNameError] = useState('');
    const [result, setResult] = useState<AgentImportResult | null>(null);
    const [pendingMcps, setPendingMcps] = useState<{
        name: string;
        url: string;
    }[]>([]);
    const [mcpTokens, setMcpTokens] = useState<Record<string, string>>({});
    const [mcpBusy, setMcpBusy] = useState<Record<string, boolean>>({});
    const [mcpDone, setMcpDone] = useState<Record<string, boolean>>({});
    const [trusted, setTrusted] = useState(false);
    // Bundled API integrations still needing this person's key - asked for the same
    // way as MCP credentials (Add now / Add later). The agent already has them on.
    const [pendingKeys, setPendingKeys] = useState<ImportedIntegration[]>([]);
    const [keyValues, setKeyValues] = useState<Record<number, string>>({});
    const [keyBusy, setKeyBusy] = useState<Record<number, boolean>>({});
    const [keyDone, setKeyDone] = useState<Record<number, boolean>>({});

    // Loading -> Preview only resolves the share and shows what it contains —
    // nothing is created yet. Import only happens once the user confirms via
    // handleConfirm, with whatever name they've settled on.
    const load = useCallback(async () => {
        if (!token)
            return;
        setStatus('loading');
        setError('');
        try {
            const { snapshot: resolved } = await resolveAgentShare(token);
            setSnapshot(resolved);
            setAgentName(resolved.name);
            setStatus('preview');
        }
        catch (err: any) {
            setError(err.message || 'This link could not be opened.');
            setStatus('error');
        }
    }, [token]);
    useEffect(() => { load(); }, [load]);

    const handleConfirm = async () => {
        if (!token || !snapshot)
            return;
        const trimmedName = agentName.trim();
        if (!trimmedName) {
            setNameError('Give this agent a name.');
            return;
        }
        setNameError('');
        setError('');
        try {
            setStatus('copying-skills');
            const skillPairs: {
                name: string;
                skill_id: string;
            }[] = [];
            for (const s of snapshot.skills) {
                const copy = await upsertMyToolWithSkillId({
                    type: 0,
                    name: s.name,
                    category: 'AI & ML',
                    icon: '',
                    description: extractSkillDescription(s.content) || '',
                    skills: '',
                    kbFileNames: [],
                    visibility: 0,
                    skillContent: s.content,
                });
                if (copy.skillId)
                    skillPairs.push({ name: s.name, skill_id: copy.skillId });
            }
            setStatus('creating-agent');
            const imported = await importAgentShare(token, skillPairs, trimmedName);
            // Carry the shared agent's logo over to the new copy. Best-effort: the
            // import has succeeded either way; without it the copy shows initials.
            try {
                await copyAgentLogoFromShare(token, imported.agent_id);
                await ensureAgentLogosLoaded(true);
            }
            catch { /* logo is cosmetic */ }
            setResult(imported);
            setPendingMcps(imported.needs_setup_mcps);
            setPendingKeys((imported.integrations ?? []).filter(i => i.needs_key && i.integration_id && !i.error));
            setStatus('done');
        }
        catch (err: any) {
            const message: string = err.message || 'This link could not be opened.';
            // Name collision (409) is recoverable — go back to the preview with the
            // name field flagged instead of dropping to the full error state, so the
            // user can just edit the name and try again.
            if (message.toLowerCase().includes('already have an agent named')) {
                setNameError(message);
                setStatus('preview');
            }
            else {
                setError(message);
                setStatus('error');
            }
        }
    };

    const handleAddCredential = async (mcp: {
        name: string;
        url: string;
    }) => {
        if (!result)
            return;
        const value = mcpTokens[mcp.url] || '';
        setMcpBusy(b => ({ ...b, [mcp.url]: true }));
        try {
            await addMcpCredential(mcp.name, mcp.url, value);
            await attachExistingMcpServer(result.agent_id, mcp.name, mcp.url);
            setMcpDone(d => ({ ...d, [mcp.url]: true }));
        }
        catch (err: any) {
            setError(err.message || `Failed to connect ${mcp.name}`);
        }
        finally {
            setMcpBusy(b => ({ ...b, [mcp.url]: false }));
        }
    };
    const dismissMcp = (url: string) => {
        setPendingMcps(list => list.filter(m => m.url !== url));
    };
    const stillPending = pendingMcps.filter(m => !mcpDone[m.url]);
    const handleAddKey = async (integration: ImportedIntegration) => {
        const id = integration.integration_id!;
        setKeyBusy(b => ({ ...b, [id]: true }));
        try {
            await saveMyCredential(id, (keyValues[id] || '').trim(), 'me');
            setKeyDone(d => ({ ...d, [id]: true }));
        }
        catch (err: any) {
            setError(err.message || `Failed to save the key for ${integration.name}`);
        }
        finally {
            setKeyBusy(b => ({ ...b, [id]: false }));
        }
    };
    const keysStillPending = pendingKeys.filter(i => !keyDone[i.integration_id!]);
    const integrationsOn = (result?.integrations ?? []).filter(i => !i.error).length;
    const integrationErrors = (result?.integrations ?? []).filter(i => i.error);

    if (status === 'preview' && snapshot && token) {
        return (<div className="mt-page">
        <ShareResolveDetailView icon="🤖" name={snapshot.name} typeLabel="Agent" description={snapshot.description ?? undefined}>
          <p style={{ color: 'var(--text-secondary)', fontSize: 12.5, margin: '0 0 14px' }}>
            You'll get your own independent copy — a fresh copy of the system prompt and skills, never a
            reference to the original. MCPs you already have credentials for are attached automatically;
            anything else is left for you to set up. API integrations come along too - your own copy, or the one
            you already have installed - and use your own API key, never the sharer's.
          </p>
          <h4 className="mt-detail-section-title">Agent bundle</h4>
          <AgentBundlePreview token={token}/>
        </ShareResolveDetailView>

        <div className="mt-detail-card" style={{ marginTop: 14, marginBottom: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Name your copy</label>
            <input className="ca-fp-editor__textarea" style={{ minHeight: 'auto', height: 38, padding: '8px 12px', fontSize: 13, width: '100%' }} value={agentName} onChange={e => { setAgentName(e.target.value); setNameError(''); }}/>
            {nameError && <p style={{ color: '#f87171', fontSize: 12, margin: '6px 0 0' }}>{nameError}</p>}
          </div>

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5, cursor: 'pointer' }}>
            <input type="checkbox" checked={trusted} onChange={e => setTrusted(e.target.checked)} style={{ marginTop: 2 }}/>
            I trust this community shared item and understand it comes from another agency in the Momentum AMP community, not from Momentum AMP directly.
          </label>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" className="mt-btn mt-btn--primary" disabled={!trusted} onClick={handleConfirm}>
              Add to Digital Workforce
            </button>
          </div>
        </div>
      </div>);
    }

    return (<div className="mt-page">
      <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            minHeight: '60vh', textAlign: 'center', gap: 14,
        }}>
      {status === 'loading' && (<>
          <FiClock size={32} style={{ color: 'var(--text-secondary)' }}/>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Opening shared agent…</p>
        </>)}

      {(status === 'copying-skills' || status === 'creating-agent') && (<>
          <FiClock size={32} style={{ color: 'var(--text-secondary)' }}/>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
            {status === 'copying-skills' && 'Copying skills…'}
            {status === 'creating-agent' && 'Creating your agent…'}
          </p>
        </>)}

      {status === 'done' && result && (<>
          <FiCheckCircle size={32} style={{ color: '#4ade80' }}/>
          <h2 style={{ color: 'var(--text-primary)', fontSize: 18, fontWeight: 700, margin: 0 }}>
            "{result.name}" created
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13, maxWidth: 420 }}>
            A brand-new agent with its own independent copy of every bundled skill.
            {result.attached_mcps.length > 0 && ` ${result.attached_mcps.length} connection${result.attached_mcps.length === 1 ? '' : 's'} attached automatically.`}
            {integrationsOn > 0 && ` ${integrationsOn} API integration${integrationsOn === 1 ? '' : 's'} turned on.`}
          </p>

          {stillPending.length > 0 && (<div style={{
                    width: '100%', maxWidth: 440, textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 10,
                    background: 'var(--surface-card)', border: '1px solid var(--border-glass)', borderRadius: 14, padding: 18, marginTop: 8,
                }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
                This agent needs {stillPending.length} more connection{stillPending.length === 1 ? '' : 's'}
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>
                Add credentials now, or come back later from the agent's MCP tab.
              </div>
              {stillPending.map(mcp => (<div key={mcp.url} style={{
                        display: 'flex', flexDirection: 'column', gap: 6, padding: '10px 12px',
                        background: 'var(--surface-glass)', border: '1px solid var(--border-glass)', borderRadius: 10,
                    }}>
                  <div style={{ fontSize: 12.5, fontWeight: 650, color: 'var(--text-primary)' }}>{mcp.name}</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input type="password" placeholder="API key / token" className="ca-fp-editor__textarea" style={{ minHeight: 'auto', height: 34, padding: '6px 10px', fontSize: 12, flex: 1 }} value={mcpTokens[mcp.url] || ''} onChange={e => setMcpTokens(t => ({ ...t, [mcp.url]: e.target.value }))}/>
                    <button className="ca-fp-editor__btn ca-fp-editor__btn--save" disabled={!!mcpBusy[mcp.url] || !mcpTokens[mcp.url]} onClick={() => handleAddCredential(mcp)}>
                      {mcpBusy[mcp.url] ? <FiRefreshCw size={12} className="ca-fp-spin"/> : 'Add now'}
                    </button>
                    <button className="ca-fp-editor__btn ca-fp-editor__btn--ghost" onClick={() => dismissMcp(mcp.url)}>
                      Add later
                    </button>
                  </div>
                </div>))}
            </div>)}

          {keysStillPending.length > 0 && (<div style={{
                    width: '100%', maxWidth: 440, textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 10,
                    background: 'var(--surface-card)', border: '1px solid var(--border-glass)', borderRadius: 14, padding: 18, marginTop: 8,
                }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
                {keysStillPending.length === 1 ? 'An API integration needs your key' : `${keysStillPending.length} API integrations need your key`}
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>
                Calls use your own key, never the sharer's. Add it now, or later from Marketplace &gt; Installed.
              </div>
              {keysStillPending.map(i => (<div key={i.integration_id} style={{
                        display: 'flex', flexDirection: 'column', gap: 6, padding: '10px 12px',
                        background: 'var(--surface-glass)', border: '1px solid var(--border-glass)', borderRadius: 10,
                    }}>
                  <div style={{ fontSize: 12.5, fontWeight: 650, color: 'var(--text-primary)' }}>{i.name}</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input type="password" placeholder="API key" autoComplete="off" className="ca-fp-editor__textarea" style={{ minHeight: 'auto', height: 34, padding: '6px 10px', fontSize: 12, flex: 1 }} value={keyValues[i.integration_id!] || ''} onChange={e => setKeyValues(v => ({ ...v, [i.integration_id!]: e.target.value }))}/>
                    <button className="ca-fp-editor__btn ca-fp-editor__btn--save" disabled={!!keyBusy[i.integration_id!] || !(keyValues[i.integration_id!] || '').trim()} onClick={() => handleAddKey(i)}>
                      {keyBusy[i.integration_id!] ? <FiRefreshCw size={12} className="ca-fp-spin"/> : 'Add now'}
                    </button>
                    <button className="ca-fp-editor__btn ca-fp-editor__btn--ghost" onClick={() => setPendingKeys(list => list.filter(x => x.integration_id !== i.integration_id))}>
                      Add later
                    </button>
                  </div>
                </div>))}
            </div>)}

          {integrationErrors.length > 0 && (<div style={{ maxWidth: 440, textAlign: 'left' }}>
              {integrationErrors.map((i, n) => (<p key={n} style={{ color: '#f87171', fontSize: 12.5, margin: '0 0 4px' }}>
                  {i.name}: {i.error}
                </p>))}
            </div>)}

          {error && <p style={{ color: '#f87171', fontSize: 12.5, margin: 0 }}>{error}</p>}

          <button type="button" className="mt-btn mt-btn--primary" style={{ marginTop: 8 }} onClick={() => navigate(ROUTES.DIGITAL_WORKFORCE)}>
            Go to Digital Workforce
          </button>
        </>)}

      {status === 'error' && (<>
          <FiAlertTriangle size={32} style={{ color: '#f87171' }}/>
          <h2 style={{ color: 'var(--text-primary)', fontSize: 16, fontWeight: 700, margin: 0 }}>Couldn't open this link</h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13, maxWidth: 420 }}>{error}</p>
        </>)}
      </div>
    </div>);
};
export default ShareAgentResolvePage;
